import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate, usePermissions } from '@/composables';
import { useErrorStore } from './errors';

export const useScheduleStore = defineStore('schedule', () => {
  const {
    toUniversalDate,
    getAsDate,
  } = useDate();
  const { showSnackMessage } = useErrorStore();
  const { can } = usePermissions();
  const { t } = useI18n();

  const selectedDate = ref<string>(sessionStorage.getItem('scheduleDate') ?? toUniversalDate(getAsDate()));
  const weekView = ref(false);
  const readOnly = computed(() => !can('manage_shifts'));
  const dayShiftPlan = ref<DayShiftPlan>([]);
  const timeBands = ref<any>();
  const loadingWeekView = ref(true);
  const loadingDayView = ref(true);
  const lastLoadedDate = ref();
  const loading = ref(false);
  const publishers = ref<App.ApiResources.PublisherResource[]>([]);
  const publisherIndex = ref<Map<number, App.ApiResources.PublisherResource>>(new Map());
  const autoScheduleStatus = ref<App.ApiResources.AutoScheduleStatus>({ stale: false, solved_at: null, status: null });
  const dayWeather = ref<App.ApiResources.DayViewWeather | null>(null);

  /**
   * The publisher lists a day view shift carries as user ids.
   *
   * A publisher who offers a day is a candidate at every location open in that
   * band, so the API names them by id and describes each person once, in the
   * response's `publishers` block. Resolving them here means components keep
   * receiving the objects they always did - and every list is covered, where
   * walking a hand-written subset used to leave `available_brothers` and
   * `adjacent_captains` without their publisher details merged in.
   */
  const candidateIdCollections = [
    'publishers',
    'available_publishers',
    'cancelled_publishers',
    'available_captains',
    'available_brothers',
    'assigned_brothers',
    'adjacent_publishers',
    'adjacent_captains',
    'limit_reached_publishers',
    'assigned_elsewhere_publishers',
  ] as const;

  function doesUserHaveDependentLinkedPublisher(
    shift: App.ApiResources.ShiftResource | null | undefined,
    publisher: App.ApiResources.PublisherResource | null | undefined,
  ) {
    if (shift === null || shift === undefined || publisher === null || publisher === undefined) {
      return [];
    }
    const childUsers = publisher?.child_users ?? [];
    if (childUsers.length === 0) {
      return [];
    }

    const assignedPublishers = shift.assigned_publishers ?? [];
    const childUserIds = childUsers.map((child) => child.id);
    const childUsersOnShift = assignedPublishers.filter((assigned_publisher) => {
      return childUserIds.includes(assigned_publisher.id);
    });

    if (childUsersOnShift.length === 0) {
      return [];
    }

    const childUsersThatWillBeRemoved: typeof childUsersOnShift = [];
    childUsersOnShift.forEach(child => {
      const childsParentUsersOnShift = assignedPublishers.filter((pub) => {
        return (pub.child_users ?? []).map((a) => a.id).includes(child.id);
      });

      if (childsParentUsersOnShift.length <= 1) {
        childUsersThatWillBeRemoved.push(child);
      }
    });
    return childUsersThatWillBeRemoved;
  }

  function hasDayShiftPlan(): boolean {
    return Array.isArray(dayShiftPlan.value) && dayShiftPlan.value.length > 0;
  }

  function bandsOverlap(
    start1: string,
    end1: string | undefined,
    start2: string,
    end2: string | undefined,
  ) {
    if (end1 === undefined || end2 === undefined) {
      return false;
    }

    return start1 < end2 && start2 < end1;
  }

  function isPublisherAssignedInTimeslot(
    shift: App.ApiResources.ShiftResource | null | undefined,
    publisher: App.ApiResources.BasicPublisherResource | null | undefined,
  ): { found: boolean; location: App.ApiResources.LocationResource | null } {
    let found = false;
    let loc: App.ApiResources.LocationResource | null = null;

    if (!shift || !publisher || !hasDayShiftPlan()) {
      return {
        found: false,
        location: null,
      };
    }

    const sameTimeslotShifts = dayShiftPlan.value.filter((shft) =>
      // Do we need to change this?
      bandsOverlap(shft.begins, shft.ends, shift.begins, shift.ends),
    );

    sameTimeslotShifts.forEach((shft) => {
      (shft.locations ?? []).forEach((location) => {
        if ((location.shift.assigned_publishers ?? []).some((pub: App.ApiResources.PublisherOnShift) => pub.id === publisher.id)) {
          found = true;
          loc = location;
        }
      });
    });

    return {
      found,
      location: loc,
    };
  }

  function doesUserHaveAdjacentShift(
    shift: App.ApiResources.ShiftResource | null | undefined,
    publisher: App.ApiResources.BasicPublisherResource | null | undefined,
  ): { found: boolean; location: App.ApiResources.LocationResource | null } {
    if (!shift || !publisher || !hasDayShiftPlan()) {
      return {
        found: false,
        location: null,
      };
    }

    const adjacentShifts = dayShiftPlan.value.filter(shiftPlan =>
      shiftPlan.ends === shift.begins || shiftPlan.begins === shift.ends,
    );

    for (const adjacentShift of adjacentShifts ?? []) {
      for (const location of adjacentShift.locations ?? []) {
        if ((location.shift.assigned_publishers ?? []).some((pub: App.ApiResources.PublisherOnShift) => pub.id === publisher.id)) {
          return { found: true, location };
        }
      }
    }

    return {
      found: false,
      location: null,
    };
  };

  function doesUserHaveAdjacentAvailability(
    shift: App.ApiResources.ShiftResource | null | undefined,
    publisher: App.ApiResources.BasicPublisherResource | null | undefined,
  ): boolean {
    if (!shift || !publisher || !hasDayShiftPlan()) {
      return false;
    }

    const adjacentShifts = dayShiftPlan.value.filter(shiftPlan =>
      shiftPlan.ends === shift.begins || shiftPlan.begins === shift.ends,
    );

    for (const adjacentShift of adjacentShifts ?? []) {
      for (const location of adjacentShift.locations ?? []) {
        if (location.shift.available_publishers?.some((pub: App.ApiResources.PublisherOnShift) => pub.id === publisher.id)) {
          return true;
        }
      }
    }

    return false;
  };

  function appendDetails(publisher: App.ApiResources.ShiftCandidateRow): App.ApiResources.PublisherOnShift {
    const extendedPub = publisherIndex.value.get(publisher.user_id);
    if (extendedPub === null || extendedPub === undefined || publisher === null || publisher === undefined) {
      // Nothing to merge with, so the row travels alone - it still carries the
      // id every caller looks it up by.
      return publisher as App.ApiResources.PublisherOnShift;
    }
    return {
      ...publisher,
      ...extendedPub,
      id: publisher.user_id,
    };
  }

  /**
   * Turn a shift's id lists back into the publisher objects components expect.
   *
   * An id the publisher block does not describe should not happen - the API
   * builds both from the same set - but a bare `{ id }` keeps a list countable
   * and searchable rather than dropping a candidate silently.
   */
  function resolveCandidateIds(ids: number[]): App.ApiResources.PublisherOnShift[] {
    return ids.map((id) => ({
      ...(publisherIndex.value.get(id) ?? {}),
      id,
      user_id: id,
    }) as App.ApiResources.PublisherOnShift);
  }

  function resolveShiftCandidates(shift: App.ApiResources.DayViewShiftResource): App.ApiResources.ShiftResource {
    const resolved = shift as unknown as App.ApiResources.ShiftResource;

    if (shift.captain != null) {
      resolved.captain = appendDetails(shift.captain);
    }

    // Assigned publishers are the only ones sent in full - the grid draws a card
    // for each of them - so they take the merge rather than the lookup.
    resolved.assigned_publishers = (shift.assigned_publishers ?? [])
      .map((publisher) => appendDetails(publisher));

    candidateIdCollections.forEach((collection) => {
      const ids = shift[collection];
      if (ids === undefined || ids === null) {
        return;
      }

      resolved[collection] = resolveCandidateIds(ids);
    });

    return resolved;
  }

  /**
   * Join shift-scoped rows - the day view's assigned publishers, the picker's
   * candidates - with the publishers they describe.
   *
   * Neither endpoint repeats a publisher's profile per shift; they name people
   * once and let this put the two halves back together.
   */
  function withPublisherDetails(
    rows: App.ApiResources.ShiftCandidateRow[],
  ): App.ApiResources.PublisherOnShift[] {
    return rows.map((row) => appendDetails(row));
  }

  function indexPublishers(list: App.ApiResources.PublisherResource[]): Map<number, App.ApiResources.PublisherResource> {
    const index = new Map<number, App.ApiResources.PublisherResource>();
    list.forEach((publisher) => {
      if (publisher.id != null) {
        index.set(publisher.id, publisher);
      }
    });

    return index;
  }

  async function pollDayShiftPlan() {
    await loadDayShiftPlan({ force: false, update: true });
  }

  async function getShiftPlanUpdates() {
    await loadDayShiftPlan({ force: true, update: true });
  }

  /**
   * Loads the day shift plan.
   *
   * @param options - The options for loading the day shift plan.
   * @param options.force - If true, forces a new request even if a request is already in progress. Defaults to true.
   * @param options.update - If true, makes a request that only refreshes data that changed after the last request. Defaults to false.
   * @param options.includeMarkers - If true, includes location markers in the response (used for map views). Defaults to false.
   */
  async function loadDayShiftPlan(options: { force?: boolean; update?: boolean; includeMarkers?: boolean }) {
    const force = options.force ?? true;
    const update = options.update ?? false;
    const includeMarkers = options.includeMarkers ?? false;
    try {
      if (!loading.value || force) {
        loading.value = true;
        sessionStorage.setItem('scheduleDate', selectedDate.value);
        const response = await axios.get<{ data: { publishers: App.ApiResources.PublisherResource[]; slots: App.ApiResources.DayViewSlotResource[]; bands: App.Models.ShiftTimeBand[]; date: string; updated_since: Date; weather?: App.ApiResources.DayViewWeather; auto_schedule?: App.ApiResources.AutoScheduleStatus } }>(`/api/shifts/schedule/day?date=${selectedDate.value}&updated_since=${update ? lastLoadedDate.value : ''}&include_markers=${includeMarkers ? '1' : '0'}`);

        const incomingPublishers = response.data.data.publishers;
        if (update) {
          const merged = new Map(publisherIndex.value);
          incomingPublishers.forEach((publisher) => {
            if (publisher.id != null) {
              merged.set(publisher.id, publisher);
            }
          });
          publisherIndex.value = merged;
          publishers.value = Array.from(merged.values());
        }
        else {
          publishers.value = incomingPublishers;
          publisherIndex.value = indexPublishers(incomingPublishers);
        }

        const dsp = response.data.data.slots;

        dsp.forEach((slot, slotIndex) => {
          (slot.locations ?? []).forEach((location) => {
            const shift = resolveShiftCandidates(location.shift);

            if (update) {
              const existing = dayShiftPlan.value[slotIndex]?.locations
                ?.find((l) => l.id === location.id);
              if (existing) {
                existing.shift = shift;
              }
            }
          });
        });

        if (!update) {
          // resolveShiftCandidates() replaces each shift's id lists in place, so by
          // here the slots hold ordinary ShiftResources again.
          dayShiftPlan.value = dsp as unknown as DayShiftPlan;
          timeBands.value = response.data.data.bands;
        }

        if (response.data.data.auto_schedule) {
          autoScheduleStatus.value = response.data.data.auto_schedule;
        }

        // Weather is not part of the `updated_since` shift delta, so it is taken
        // from every response - including polls - and stays current for every
        // location whether or not its shifts changed.
        if (response.data.data.weather) {
          dayWeather.value = response.data.data.weather;
        }

        lastLoadedDate.value = response.data.data.updated_since;
        loading.value = false;
      }
    }
    catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
        showSnackMessage(`${t('shift.detail.cannot_load')}: ${selectedDate.value}`);
        loading.value = false;
      }
    }
  }

  return {
    loadDayShiftPlan,
    pollDayShiftPlan,
    bandsOverlap,
    getShiftPlanUpdates,
    doesUserHaveAdjacentShift,
    doesUserHaveAdjacentAvailability,
    isPublisherAssignedInTimeslot,
    doesUserHaveDependentLinkedPublisher,
    withPublisherDetails,
    dayWeather,
    dayShiftPlan,
    loadingDayView,
    loadingWeekView,
    selectedDate,
    weekView,
    publishers,
    readOnly,
    lastLoadedDate,
    autoScheduleStatus,
  };
});
